import ShoppingCartIcon from '@icons/ShoppingCartIcon'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { CART_URL } from '../../../urls'
import { SubmitButton } from './common'

const HIDE_AFTER_MS = 5000

// "Go to Shopping Bag" is the outlined button above "Continue Shopping", with the border of the KIT/PDF toggle.
const outlinedSx = {
  border: '1.5px solid',
  borderColor: 'secondary.main',
  '&:hover': { border: '1.5px solid', borderColor: 'secondary.main', bgcolor: '#eafaf9' },
} as const

interface Props {
  onClose(): void
}

// Shown over the panel's form (which keeps the panel's height) after adding to the cart, then the panel closes.
// The countdown bar at the bottom drives the closing: it pauses while hovered, and stops for good on a touch.
// "Continue Shopping" or a click anywhere outside the notice closes it right away.
const AddedToCartNotice: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const ref = useRef<HTMLDivElement>(null)
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose()
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [onClose])

  return (
    <div
      ref={ref}
      className={`product-card-added ${touched ? 'product-card-added--paused' : ''}`}
      role='status'
      onTouchStart={() => setTouched(true)}
    >
      <p className='product-card-added__title'>{t('productCard.addedToCart', 'Added to cart!')}</p>
      <SubmitButton
        variant='outlined'
        icon={<ShoppingCartIcon viewBox='0 -960 960 960' />}
        onClick={() => history.push(CART_URL)}
        sx={outlinedSx}
      >
        {t('productCard.goToShoppingBag', 'Go to Shopping Bag')}
      </SubmitButton>
      <SubmitButton onClick={onClose}>{t('button.continueShopping', 'Continue Shopping')}</SubmitButton>
      <div
        className='product-card-added__countdown'
        style={{ animationDuration: `${HIDE_AFTER_MS}ms` }}
        onAnimationEnd={onClose}
      />
    </div>
  )
}

export default AddedToCartNotice
